import { FC } from 'react';
import {
  Content,
  isFilled,
  KeyTextField,
  LinkField,
  RichTextField,
} from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  CardsComparisonSlice2Cards,
  CardsComparisonSliceDefault,
  CardsComparisonSliceFeatured,
  CardsComparisonSliceWithImages,
} from '../../../prismicio-types';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { TitleSection } from '@/components';

enum SLICE_VARIATION {
  twoCards = '2Cards',
  default = 'default',
  withImages = 'withImages',
  featured = 'featured',
  theChoiceDualRiskCtas = 'theChoiceDualRiskCtas',
}

/**
 * Props for `CardsComparison`.
 */
export type CardsComparisonProps =
  SliceComponentProps<Content.CardsComparisonSlice>;

// ── TheChoiceDualRiskCtas variation ─────────────────────────────────────────

const TheChoiceDualRiskCtas: FC<{
  slice: Content.CardsComparisonSliceTheChoiceDualRiskCtas;
}> = ({ slice }) => {
  const {
    eyebrow,
    cards,
    main_message,
    secondary_message,
    primary_button,
    secondary_button,
    background_pattern,
  } = slice.primary;

  return (
    <section
      data-slice-type="cards_comparison"
      data-slice-variation="theChoiceDualRiskCtas"
      className="py-12 md:py-16"
    >
      <div className="relative mx-[30px] overflow-hidden rounded-[24px] border border-[#00f557]/30 bg-[#031a14] text-white shadow-[0_0_60px_rgba(0,245,87,0.08)]">
        {isFilled.image(background_pattern) && (
          <PrismicNextImage
            field={background_pattern}
            fill
            className="pointer-events-none absolute inset-0 object-cover opacity-40"
            fallbackAlt=""
          />
        )}

        <div className="relative z-10 flex flex-col items-center gap-8 px-6 py-12 md:px-12 md:py-16">
          {eyebrow && (
            <div className="inline-flex rounded-full border border-[#00f557] px-5 py-2">
              <span className="text-[14px] font-semibold uppercase leading-[1.2] tracking-wide text-white">
                {eyebrow}
              </span>
            </div>
          )}

          {/* 2-column choice cards */}
          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
            {cards?.map((card, i) => {
              const isDanger = card.card_color === 'danger';
              const tint = isDanger ? '#ff5a4d' : '#00f557';
              return (
                <div
                  key={i}
                  className="flex items-center gap-4 rounded-2xl border bg-black/40 p-6 backdrop-blur-md"
                  style={{ borderColor: `${tint}80` }}
                >
                  {card.icon?.url && (
                    <PrismicNextImage
                      field={card.icon}
                      className="h-12 w-12 shrink-0 object-contain"
                      fallbackAlt=""
                    />
                  )}
                  {isFilled.richText(card.text) && (
                    <div
                      className="text-[18px] font-semibold leading-[1.3] md:text-[20px]"
                      style={{ color: tint }}
                    >
                      <PrismicRichText
                        field={card.text}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="m-0">{children}</p>
                          ),
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {isFilled.richText(main_message) && (
            <div className="max-w-[760px] text-center text-[16px] font-semibold leading-[1.5] text-white md:text-[18px]">
              <PrismicRichText field={main_message} />
            </div>
          )}

          {isFilled.richText(secondary_message) && (
            <div className="max-w-[760px] text-center text-[14px] leading-[1.6] text-white/80 md:text-[15px]">
              <PrismicRichText field={secondary_message} />
            </div>
          )}

          {(isFilled.link(primary_button) ||
            isFilled.link(secondary_button)) && (
            <div className="flex flex-col items-center gap-4 sm:flex-row">
              {isFilled.link(primary_button) && (
                <PrismicNextLink
                  field={primary_button}
                  className="inline-flex items-center justify-center rounded-full border border-white/60 px-7 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-white/10 md:text-[15px]"
                >
                  {primary_button.text || 'Learn more'}
                </PrismicNextLink>
              )}
              {isFilled.link(secondary_button) && (
                <PrismicNextLink
                  field={secondary_button}
                  className="inline-flex items-center justify-center rounded-full bg-[#00f557] px-7 py-3 text-[14px] font-bold text-[#002726] transition-opacity hover:opacity-90 md:text-[15px]"
                >
                  {secondary_button.text || 'Explore'}
                </PrismicNextLink>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ── Main component (dispatches by variation) ────────────────────────────────

/**
 * Component for "CardsComparison" Slices.
 */
const CardsComparison = ({ slice }: CardsComparisonProps): JSX.Element => {
  if (slice.variation === SLICE_VARIATION.theChoiceDualRiskCtas) {
    return <TheChoiceDualRiskCtas slice={slice} />;
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="px-5 md:px-16 py-20 max-w-screen-2xl mx-auto"
    >
      {slice.variation === SLICE_VARIATION.twoCards && (
        <TwoCards slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.default && (
        <ThreeCards slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.withImages && (
        <CardsWithImages slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.featured && (
        <FeaturedCards slice={slice} />
      )}
    </section>
  );
};

const FeaturedCards = ({ slice }: { slice: CardsComparisonSliceFeatured }) => {
  const {
    primary: { title, description, cards_style: cardsStylePrismic, cards },
  } = slice;

  const getCardStyle = (cardStyle: string) => {
    switch (cardStyle) {
      case 'black':
      default:
        return {
          container: 'card p-6 flex flex-col gap-y-4',
          content: '',
        };
      case 'white':
        return {
          container: 'bg-white-gradient',
          content: 'text-primary-darker-green-black',
        };
    }
  };

  return (
    <div className="text-white">
      <TitleSection title={title} description={description} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {cards.map((card, index) => (
          <div
            className={`rounded-[20px] last:md:col-span-2 last:lg:col-span-1 ${getCardStyle(cardsStylePrismic).container} ${card.image.url ? '' : 'p-8'}`}
            key={`card-featured-${index}`}
          >
            {card.icon && <PrismicNextImage field={card.icon} />}
            {card.image && (
              <PrismicNextImage
                field={card.image}
                className="rounded-t-[20px]"
              />
            )}
            <div
              className={`flex flex-col gap-y-8 ${getCardStyle(cardsStylePrismic).content} ${card.image.url ? 'p-8' : ''}`}
            >
              <PrismicRichText field={card.title} />
              <PrismicRichText field={card.description} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CardsWithImages = ({
  slice,
}: {
  slice: CardsComparisonSliceWithImages;
}): JSX.Element => {
  const {
    primary: { title, subtitle, cards },
  } = slice;
  return (
    <div className="text-white">
      <TitleSection title={title} description={subtitle} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {cards.map(
          (
            {
              title,
              description,
              button_label: buttonLabel,
              button_link: buttonLink,
              image,
            },
            index,
          ) => (
            <div key={`card-${index}`} className="card flex-col">
              <div className="p-6 md:p-12 flex flex-col gap-y-6">
                <PrismicRichText field={title} />
                <PrismicRichText field={description} />
                <PrismicNextLink
                  field={buttonLink}
                  className="button-outline-primary"
                >
                  <span>{buttonLabel}</span>
                </PrismicNextLink>
              </div>
              <PrismicNextImage field={image} className="rounded-b-[20px]" />
            </div>
          ),
        )}
      </div>
    </div>
  );
};

const ThreeCards = ({
  slice,
}: {
  slice: CardsComparisonSliceDefault;
}): JSX.Element => {
  const {
    primary: {
      footer_message: footerMessage,
      button_label: buttonLabel,
      button_link: buttonLink,
      cards,
      title,
      description,
    },
  } = slice;
  return (
    <div className="text-white">
      <TitleSection title={title} description={description} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {cards.map(({ title, list_items: listItems }, index) => (
          <div
            key={`card-${index}`}
            className="card p-8 md:last:col-span-2 lg:last:col-span-1"
          >
            <PrismicRichText field={title} />
            <Divider bgColor="bg-button-gradient" />
            <PrismicRichText
              field={listItems}
              components={{
                list: ({ children }) => (
                  <ul className="checklist">{children}</ul>
                ),
              }}
            />
          </div>
        ))}
      </div>
      <FooterSection
        footerMessage={footerMessage}
        buttonLabel={buttonLabel}
        buttonLink={buttonLink}
      />
    </div>
  );
};

const TwoCards = ({
  slice,
}: {
  slice: CardsComparisonSlice2Cards;
}): JSX.Element => {
  const {
    primary: {
      title,
      subtitle,
      cards,
      footer_message: footerMessage,
      button_label: buttonLabel,
      button_link: buttonLink,
    },
  } = slice;
  return (
    <div className="text-white">
      <TitleSection title={title} description={subtitle} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cards.map(
          (
            {
              title,
              button_label: buttonLabel,
              button_link: buttonLink,
              list_items: listItems,
            },
            index,
          ) => (
            <div key={`card-${index}`} className="card p-8 text-center">
              <div className={index === 1 ? 'text-[#05F273]' : ''}>
                <PrismicRichText field={title} />
              </div>
              <Divider bgColor="bg-button-gradient" />
              {buttonLabel && (
                <PrismicNextLink
                  field={buttonLink}
                  className="button-bordered w-full md:w-1/2 text-center"
                >
                  <span>{buttonLabel}</span>
                </PrismicNextLink>
              )}
              <PrismicRichText
                field={listItems}
                components={{
                  list: ({ children }) => (
                    <ul className="checklist">{children}</ul>
                  ),
                }}
              />
            </div>
          ),
        )}
      </div>
      <FooterSection
        footerMessage={footerMessage}
        buttonLink={buttonLink}
        buttonLabel={buttonLabel}
      />
    </div>
  );
};

const FooterSection = ({
  footerMessage,
  buttonLink,
  buttonLabel,
}: {
  footerMessage: RichTextField;
  buttonLink: LinkField;
  buttonLabel: KeyTextField;
}): JSX.Element => {
  return (
    <div className="flex flex-col items-center mt-12 gap-y-12">
      <PrismicRichText
        field={footerMessage}
        components={{
          paragraph: ({ children }) => (
            <p className="text-medium-normal">{children}</p>
          ),
        }}
      />
      <PrismicNextLink field={buttonLink} className="button-bordered">
        <span>{buttonLabel}</span>
      </PrismicNextLink>
    </div>
  );
};

const Divider = ({ bgColor }: { bgColor: string }): JSX.Element => {
  return <div className={`w-full h-[1px] ${bgColor}`}></div>;
};

export default CardsComparison;
